import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Building2, Plus, Send, Mail, Phone, User, Power } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = { company_name: '', contact_name: '', email: '', phone: '' };

export default function AdminClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const fetchClients = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/clients`);
      setClients(res.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const createClient = async () => {
    if (!form.company_name.trim() || !form.contact_name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error('All fields are required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/clients`, form);
      toast.success(res.data.email_sent ? 'Invitation sent to the client' : 'Client created — email could not be sent, use Resend later');
      setDialogOpen(false);
      setForm(EMPTY_FORM);
      await fetchClients();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create client');
    } finally {
      setSubmitting(false);
    }
  };

  const resendInvite = async (client) => {
    try {
      const res = await axios.post(`${API}/admin/clients/${client.id}/resend-invite`);
      toast.success(res.data.email_sent ? `Invitation resent to ${client.email}` : 'Could not send email — email service unavailable');
      await fetchClients();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to resend invitation');
    }
  };

  const toggleStatus = async (client) => {
    const next = client.status === 'active' ? 'disabled' : 'active';
    try {
      await axios.put(`${API}/admin/clients/${client.id}`, { status: next });
      toast.success(next === 'disabled' ? 'Client access disabled' : 'Client access re-enabled');
      await fetchClients();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update client');
    }
  };

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 space-y-4 max-w-4xl mx-auto">
        <div className="border-b border-border pb-4 flex items-center gap-3">
          <Building2 className="w-6 h-6 text-primary" strokeWidth={1.5} />
          <div>
            <h1 className="text-2xl font-barlow tracking-widest text-foreground">CLIENTS</h1>
            <p className="text-xs text-muted-foreground mt-1">
              Invite companies to the Client Staffing Portal. Clients only see their own portal.
            </p>
          </div>
          <Button
            onClick={() => setDialogOpen(true)}
            data-testid="add-client-btn"
            className="ml-auto bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm uppercase tracking-wider text-xs font-semibold"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Add Client
          </Button>
        </div>

        {loading ? (
          <p className="py-12 text-center text-muted-foreground text-sm">Loading…</p>
        ) : clients.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground text-sm" data-testid="clients-empty">
            No clients yet. Click <strong>Add Client</strong> to send your first invitation.
          </div>
        ) : (
          <div className="space-y-3">
            {clients.map((c) => (
              <Card key={c.id} className="bg-card border-border rounded-sm" data-testid={`client-row-${c.id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-primary shrink-0" />
                        {c.company_name}
                        <Badge
                          className={`text-[10px] uppercase tracking-wider border ${
                            c.status === 'active' ? 'bg-green-500/10 text-green-400 border-green-500/30'
                            : c.status === 'invited' ? 'bg-yellow-500/10 text-yellow-300 border-yellow-500/30'
                            : 'bg-red-500/10 text-red-400 border-red-500/30'
                          }`}
                          data-testid={`client-status-${c.id}`}
                        >
                          {c.status}
                        </Badge>
                      </div>
                      <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-3 gap-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1.5"><User className="w-3 h-3" />{c.contact_name}</span>
                        <span className="flex items-center gap-1.5 break-all"><Mail className="w-3 h-3 shrink-0" />{c.email}</span>
                        <span className="flex items-center gap-1.5"><Phone className="w-3 h-3" />{c.phone}</span>
                      </div>
                      {c.invite_sent_at && c.status === 'invited' && (
                        <p className="mt-1 text-[10px] text-muted-foreground">
                          Invited {(() => { try { return format(parseISO(c.invite_sent_at), 'dd MMM yyyy HH:mm'); } catch { return c.invite_sent_at; } })()}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {c.status === 'invited' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => resendInvite(c)}
                          data-testid={`resend-invite-${c.id}`}
                          className="border-primary/40 text-primary hover:bg-primary/10 rounded-sm text-xs"
                        >
                          <Send className="w-3.5 h-3.5 mr-1" /> Resend Invite
                        </Button>
                      )}
                      {c.status !== 'invited' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleStatus(c)}
                          data-testid={`toggle-client-${c.id}`}
                          className={`rounded-sm text-xs ${c.status === 'active' ? 'border-red-500/40 text-red-400 hover:bg-red-500/10' : 'border-green-500/40 text-green-400 hover:bg-green-500/10'}`}
                        >
                          <Power className="w-3.5 h-3.5 mr-1" />
                          {c.status === 'active' ? 'Disable' : 'Enable'}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add client dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border rounded-sm" data-testid="add-client-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow tracking-widest uppercase text-primary text-lg">
              Invite a client
            </DialogTitle>
            <DialogDescription>
              The client receives an email invitation to create their Client Portal account.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-1">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider">Company Name</Label>
              <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="e.g. Hull Events Ltd" className="rounded-sm" data-testid="client-company-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider">Contact Name</Label>
              <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="e.g. John Smith" className="rounded-sm" data-testid="client-contact-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider">Email Address</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="client@company.com" className="rounded-sm" data-testid="client-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider">Phone Number</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="07xxx xxxxxx" className="rounded-sm" data-testid="client-phone-input" />
            </div>
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting} className="rounded-sm">Cancel</Button>
            <Button
              onClick={createClient}
              disabled={submitting}
              data-testid="send-client-invite-btn"
              className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm uppercase tracking-wider text-xs font-semibold"
            >
              <Send className="w-4 h-4 mr-1.5" />
              {submitting ? 'Sending…' : 'Send Invitation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
